import { motion } from 'framer-motion';
import classNames from 'classnames';
import {
	IconCheck,
	IconClipboardCheck,
	IconSearch,
	IconTargetArrow,
	IconThumbUp,
	IconUsers,
} from '@tabler/icons-react/dist/cjs/tabler-icons-react';
import { Button } from '@shared/ui/button/button';
import { DEFAULT_TEACHER_MATCHING_DELAY_MS, matchingBodyVariants } from '../lib/teacher-matching-progress';
import s from '../teacher-matching-progress.module.scss';

const matchingSteps = [
	{
		icon: IconTargetArrow,
		title: <>Анализируем цели и предпочтения</>,
		text: <>Изучаем, чему вы хотите научиться и какой у вас опыт.</>,
	},
	{
		icon: IconSearch,
		title: <>Сопоставляем с нашей базой</>,
		text: <>Ищем преподавателей, которые подходят под ваши критерии.</>,
	},
	{
		icon: IconClipboardCheck,
		title: <>Проверяем доступность</>,
		text: <>Учитываем расписание преподавателей и ваши пожелания.</>,
	},
	{
		icon: IconThumbUp,
		title: <>Формируем лучшие варианты</>,
		text: <>Отбираем для вас несколько лучших преподавателей.</>,
	},
];

const getSecondWord = (value) => {
	const lastTwoDigits = value % 100;
	const lastDigit = value % 10;

	if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return 'секунд';
	if (lastDigit === 1) return 'секунда';
	if (lastDigit >= 2 && lastDigit <= 4) return 'секунды';

	return 'секунд';
};

const getStepState = ({ progress, index, total }) => {
	const stepStart = (index / total) * 100;
	const stepEnd = ((index + 1) / total) * 100;

	if (progress >= stepEnd) return 'completed';
	if (progress >= stepStart || index === 0) return 'active';

	return 'pending';
};

export const MatchingProgressBody = ({ progress, onSkip }) => {
	const normalizedProgress = Math.min(Math.max(progress || 0, 0), 100);
	const remainingSeconds = Math.max(
		Math.ceil(((100 - normalizedProgress) / 100) * (DEFAULT_TEACHER_MATCHING_DELAY_MS / 1000)),
		0
	);

	const handleSkip = (e) => {
		e.preventDefault()
		onSkip?.()
	}

	return (
		<motion.div
			key="progress"
			className={s.matchingBody}
			variants={matchingBodyVariants}
			initial="initial"
			animate="animate"
			exit="exit"
		>
			<div className={s.matchingTop}>
				<h3 className={s.matchingProgressTitle}>Ход подбора</h3>
				<p className={s.matchingTimer}>Осталось&nbsp; {remainingSeconds} {getSecondWord(remainingSeconds)}</p>
			</div>

			<div className={s.matchingSteps}>
				{matchingSteps.map((step, index) => {
					const state = getStepState({ progress: normalizedProgress, index, total: matchingSteps.length });
					const isCompleted = state === 'completed';
					const isActive = state === 'active';
					const StepIcon = step.icon;

					return (
						<motion.div
							key={index}
							className={classNames(
								s.matchingStep,
								isCompleted ? s.completed : '',
								isActive ? s.active : ''
							)}
							initial={false}
							animate={{ opacity: state === 'pending' ? 0.72 : 1 }}
							transition={{ duration: 0.25 }}
						>
							<div className={s.matchingStepHeader}>
								<div className={s.matchingStepTitleWrapper}>
									<div className={s.matchingStepIcon}>
										<StepIcon size={24} stroke={1.8}/>
									</div>
									<h4>{step.title}</h4>
								</div>
								<div className={s.matchingStepStatus}>
									{isCompleted ? <IconCheck size={22} stroke={3}/> : index + 1}
								</div>
							</div>
							<div className={s.matchingStepContent}>
								<h4>{step.title}</h4>
								<p>{step.text}</p>
							</div>
						</motion.div>
					);
				})}
			</div>

			<div className={s.matchingTeachersCta}>
				<div className={s.matchingTeachersHint}>
					<div className={s.matchingTeachersIcon}>
						<IconUsers size={24} stroke={1.8}/>
					</div>
					<div>
						<h4>Хотите посмотреть всех преподавателей прямо сейчас?</h4>
						<p>Вы можете изучить базу самостоятельно.</p>
					</div>
				</div>
				<Button
					to="/teachers"
					theme="secondary"
					className={s.matchingTeachersButton}
					label="Посмотреть всех преподавателей"
				/>
			</div>

			<button type="button" className={s.skipButton} onClick={handleSkip}>
				Пропустить подбор
			</button>
		</motion.div>
	);
};
